import React, { useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import { RootStoreContext } from 'index';
import MdaDialog from 'components/MainLayout/Mda/MdaDialog/MdaDialog';
import { ApplicationRoute } from 'routes/routes.enums';
import { PrequalificationEntrySource } from 'components/prequalification/RegistrationProcess/registrationProcess.enums';
import Spinner from 'components/common/Spinner/Spinner';
import { hasPermission } from 'components/permissions/permissions.utils';
import { PermissionName } from 'components/permissions/permissions.enum';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { notify } from 'components/common/Toast/toast.helpers';

import styles from './AccountStatus.module.scss';
import AccountStatusHeader from './accountStatusHeader/AccountStatusHeader';
import { ASSIGNING_CONTACT_TEXT } from './accountStatus.constants';
import { SectionStatus, StatusSectionName } from './accountStatus.enums';
import AccountStatusSection from './accountStatusSection/AccountStatusSection';
import { getAccountHeaderStatus, getSectionStatus } from './accountStatus.utils';

const AccountStatus = () => {
  const [isOpenMda, setIsOpenMda] = useState(false);
  const navigator = useNavigate();
  const { intermediaryApplicationsStore, prequalificationStore, authStore, intermediaryStore, globalModalsStore } =
    useContext(RootStoreContext);
  const { companyProfile, isLoading, getCompanyProfile, isAwaitingTeamMemberAssignment } =
    intermediaryApplicationsStore;
  const { openFeatureRestricted } = globalModalsStore;
  const { setPrequalificationEntrySource } = prequalificationStore;
  const { setIntakeMeetingScheduled, intakeMeetingScheduled } = authStore;
  const { account } = authStore;

  const contractorRegistrationStatus = getSectionStatus(StatusSectionName.ContractorRegistration, companyProfile);
  const mdaStatus = getSectionStatus(StatusSectionName.MasterDealerAgreement, companyProfile);
  const intakeMeetingStatus = getSectionStatus(StatusSectionName.IntakeMeeting, companyProfile);
  const accountHeaderStatus = getAccountHeaderStatus(companyProfile);

  useEffect(() => {
    const getInitData = async () => {
      await getCompanyProfile();
    };

    getInitData();
  }, []);

  const onContinueRegistration = () => {
    setPrequalificationEntrySource(PrequalificationEntrySource.Portal);

    navigator(ApplicationRoute.PortalRegistration);
  };

  const onOpenMda = () => {
    const canUpdateCompanyVerification = hasPermission(account!.permissions, [
      PermissionName.CompanyVerificationUpdate,
    ]);

    if (canUpdateCompanyVerification) {
      setIsOpenMda(true);
    } else {
      openFeatureRestricted();
    }
  };

  const onScheduleMeeting = async () => {
    try {
      if (!companyProfile?.intakeMeetingBookingLink) {
        setIntakeMeetingScheduled(true);

        await intermediaryStore.scheduleMeeting();

        notify(ToastMessage.YourRequestToScheduleCallReceived, 'success');

        return;
      }

      const decodedUrl = decodeURIComponent(companyProfile?.intakeMeetingBookingLink || '');

      window.open(decodedUrl, '_blank', 'noreferrer');
    } catch (err) {
      notify(ToastMessage.DefaultError, 'error');
    }
  };

  if (isLoading && !isAwaitingTeamMemberAssignment) {
    return <Spinner />;
  }

  const getIntakeMeetingStatus = (): SectionStatus => {
    if (intakeMeetingStatus === SectionStatus.Active && intakeMeetingScheduled) {
      return SectionStatus.Disabled;
    }

    return intakeMeetingStatus;
  };

  return (
    <div className={styles.pageContainer}>
      <AccountStatusHeader status={accountHeaderStatus} />
      <div className={styles.statusesBlock}>
        <AccountStatusSection
          name={StatusSectionName.ContractorRegistration}
          status={contractorRegistrationStatus}
          handleClick={onContinueRegistration}
        />
        <AccountStatusSection
          name={StatusSectionName.MasterDealerAgreement}
          status={mdaStatus}
          handleClick={onOpenMda}
        />
        <AccountStatusSection
          name={StatusSectionName.IntakeMeeting}
          status={isAwaitingTeamMemberAssignment ? SectionStatus.Disabled : getIntakeMeetingStatus()}
          handleClick={onScheduleMeeting}
          statusText={isAwaitingTeamMemberAssignment ? ASSIGNING_CONTACT_TEXT : undefined}
        />
      </div>
      <MdaDialog isOpen={isOpenMda} setIsOpen={setIsOpenMda} />
    </div>
  );
};

export default observer(AccountStatus);
